import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Typography, IconButton, Slide } from "@mui/material";
import { InstallDesktop, Close } from "@mui/icons-material";

/**
 * PWA Install Prompt — shows a banner when the app is installable.
 * Captures the beforeinstallprompt event and offers a native install button.
 * Dismissable — won't show again for the session.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Hide if already installed
  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      setDeferredPrompt(null);
    }
    setDismissed(true);
  };

  if (!deferredPrompt || dismissed) return null;

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1400,
          p: 2,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
          color: "#fff",
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <InstallDesktop sx={{ fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Install WillGates
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Add to home screen for quick access & offline support
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleInstall}
          sx={{
            bgcolor: "#fff",
            color: "#1a237e",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
            "&:hover": { bgcolor: "#e8eaf6" },
          }}
        >
          Install
        </Button>
        <IconButton size="small" onClick={() => setDismissed(true)} sx={{ color: "#fff" }}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Slide>
  );
}
